import { moduleHeadroomMm, shelvingCellHeadroomMm } from "./flapHeadroom";
import { chestTopEnamel, enamelColorLabel, shelvingCarcassEnamel, type EnamelFinish } from "./shelvingFinishes";
import { CARCASS_MM } from "./carcass";
import { chestCellSplit, chestPlinthEnamel, normalizeTopDecor } from "./chest";
import type { PlannerProject, WardrobeModule } from "../types";
import { cabinetDrawerLayout } from "./cabinetDrawers";
import {
  cornerMezzanineMm,
  cornerPlan,
  cornerPlinthPlan,
  cornerShelfSize,
  cornerBlindPanelSpan,
  cornerGapCoverSpan,
  cornerHingePostSpan,
  CORNER_HINGE_POST_DEPTH_MM
} from "./corner";
import { normalizeShelfDecor } from "./interiorDecor";
import { END_MODULE_BACK_MM, endModulePlateAreaM2, endModuleShape, endModuleTierCount } from "./endModules";
import {
  cabinetBaseHeightMm,
  cabinetBaseSetbackMm,
  cabinetBaseType,
  leftFillerMm,
  rightFillerMm,
  topFillerMm
} from "./installation";
import {
  baseModuleFor,
  cabinetSectionWidths,
  hasUnifiedFacade,
  hingedDoorLeafLayout,
  mezzanineFlapLayout,
  MAX_SLIDING_DOOR_WIDTH_MM,
  mezzanineFor
} from "./moduleRules";
import {
  getShelvingCellBoards,
  getShelvingCellLayouts,
  getShelvingDiagonalSections,
  getShelvingGridLayout,
  getShelvingMainDividerSegment,
  getShelvingSpanSections,
  shelvingCellBackPanelSizeMm,
  shelvingLatticeBoards,
  shelvingSideHeightMm,
  SHELVING_BACK_PANEL_THICKNESS_MM,
  SHELVING_PANEL_THICKNESS_MM,
  SHELVING_PLINTH_HEIGHT_MM,
  type ShelvingCellLayout,
  type ShelvingGridLayout
} from "./shelving";

/**
 * Детали изделия так, как они построены в 3D. Из этого списка считается смета
 * и пишутся строки техкарты — чтобы цена, чертёж и картинка не расходились.
 * Размеры: длина × ширина × толщина, мм (как в техкарте).
 */
export type BuiltPanelKind = "board" | "back";

export interface BuiltPanel {
  name: string;
  count: number;
  lengthMm: number;
  widthMm: number;
  thicknessMm: number;
  /** board — плита корпуса, back — задняя стенка (может быть в своём декоре). */
  kind: BuiltPanelKind;
  /** Площадь одной фигурной детали, м²; у прямоугольной — длина × ширина. */
  shapeAreaM2?: number;
  note?: string;
  /** Плита в своём декоре Egger (столешница комода) — и по цене своей группы. */
  decor?: string;
  /** МДФ в эмали в цвет фасадов (цоколь комода): по цене эмали, без фрезеровки. */
  painted?: true;
}

const panel = (
  name: string,
  count: number,
  lengthMm: number,
  widthMm: number,
  extra: Partial<BuiltPanel> = {}
): BuiltPanel => ({
  name,
  count,
  lengthMm,
  widthMm,
  thicknessMm: CARCASS_MM,
  kind: "board",
  ...extra
});

/** Площадь всех деталей строки, м². */
export const builtPanelAreaM2 = (part: BuiltPanel) =>
  part.count * (part.shapeAreaM2 ?? (part.lengthMm * part.widthMm) / 1_000_000);

export const builtPanelsAreaM2 = (parts: BuiltPanel[], kind?: BuiltPanelKind) =>
  parts
    .filter((part) => !kind || part.kind === kind)
    .reduce((sum, part) => sum + builtPanelAreaM2(part), 0);

export const builtPanelSize = (part: BuiltPanel) =>
  `${Math.round(part.lengthMm)} × ${Math.round(part.widthMm)} × ${part.thicknessMm} мм`;

/**
 * Цоколь — замкнутая рамка, как в 3D: передняя планка уже корпуса на 28 мм,
 * боковые — от неё до стены, задняя — у стены между боковыми. Без задней
 * планки сзади под шкафом была видна прорезь (приёмка 08.10).
 */
const plinthPanels = (width: number, depth: number, height: number, setback: number): BuiltPanel[] => [
  panel("Цоколь: передняя планка", 1, Math.max(80, width - 28), height, {
    note: `Утоплена на ${setback} мм от фасада`
  }),
  panel("Цоколь: боковые планки", 2, Math.max(80, depth - setback), height, {
    note: "От передней планки до стены"
  }),
  panel("Цоколь: задняя планка", 1, plinthBackPlankMm(width), height, {
    note: "У стены, между боковыми планками"
  })
];

/**
 * Цоколь стеллажа и комода — заподлицо (27.10): боковины стоят на полу, передняя
 * планка между ними вровень с их передней кромкой, боковые — за ней.
 */
export const SHELVING_PLINTH_SETBACK_MM = 0;
const shelvingPlinthPanels = (module: WardrobeModule): BuiltPanel[] => {
  const height = SHELVING_PLINTH_HEIGHT_MM;
  return [
    panel("Цоколь: передняя планка", 1, Math.max(80, module.width - SHELVING_PANEL_THICKNESS_MM * 2), height, {
      note: "Заподлицо с передней кромкой боковин, между ними"
    }),
    panel("Цоколь: боковые планки", 2, Math.max(80, module.depth - SHELVING_PANEL_THICKNESS_MM), height, {
      note: "От передней планки до стены"
    }),
    panel("Цоколь: задняя планка", 1, plinthBackPlankMm(module.width), height, {
      note: "У стены, между боковыми планками"
    })
  ];
};

/** Задняя планка цоколя — между боковыми (их оси в 23 мм от краёв корпуса). */
export const plinthBackPlankMm = (width: number) => Math.max(40, width - 2 * (23 + CARCASS_MM / 2));

// ---------------------------------------------------------------------------
// Шкаф и антресоль

const isCabinet = (module: WardrobeModule) =>
  module.type === "single" || module.type === "double" || module.type === "mezzanine";

/** Боковины, крышка и дно, задняя стенка, общие стойки. */
export const cabinetBodyPanels = (module: WardrobeModule): BuiltPanel[] => {
  if (!isCabinet(module)) return [];
  const base = module.type === "mezzanine" ? 0 : cabinetBaseHeightMm(module);
  const legs = module.type !== "mezzanine" && cabinetBaseType(module) === "legs";
  const sideHeight = legs ? module.height - base : module.height;
  const innerWidth = Math.max(0, module.width - CARCASS_MM * 2);
  const bodyHeight = Math.max(0, module.height - base - CARCASS_MM * 2);
  const parts = [
    panel("Боковые панели", 2, sideHeight, module.depth),
    panel("Верхняя и нижняя панели", 2, innerWidth, module.depth),
    panel("Задняя стенка", 1, innerWidth, bodyHeight, { thicknessMm: 8, kind: "back" })
  ];
  const sections = cabinetSectionWidths(module.width);
  if (sections.length > 1) {
    parts.push(panel("Внутренние стойки", sections.length - 1, bodyHeight, module.depth, {
      note: `Один общий корпус; пролёты ${sections.join(" / ")} мм`
    }));
  }
  return parts;
};

/**
 * Отделка передней планки цоколя шкафа (видео Сергея 23.10): в цвет фасадов,
 * без фрезеровки. МДФ в эмали — эмалью того же цвета, ЛДСП — в декоре фасадов.
 * Без дверей, со стеклом или зеркалом — из плиты корпуса (null).
 */
export type CabinetPlinthFinish =
  | { kind: "enamel"; facade: "satin" | "gloss"; facadeColor?: WardrobeModule["facadeColor"]; facadeColorHex?: string; facadeColorCode?: string }
  | { kind: "decor"; decor?: string };
export const cabinetPlinthFinish = (module: WardrobeModule): CabinetPlinthFinish | null => {
  if (module.type !== "single" && module.type !== "double" && module.type !== "corner45") return null;
  if (module.door !== "hinged") return null;
  if (module.facade === "satin" || module.facade === "gloss") {
    return { kind: "enamel", facade: module.facade, facadeColor: module.facadeColor,
      facadeColorHex: module.facadeColorHex, facadeColorCode: module.facadeColorCode };
  }
  if (module.facade === "wood") return { kind: "decor", decor: module.facadeDecor };
  return null;
};

/** Цоколь шкафа: передняя планка и две боковые. На ножках и без цоколя — нет. */
export const cabinetPlinthPanels = (module: WardrobeModule): BuiltPanel[] => {
  if (module.type !== "single" && module.type !== "double") return [];
  const height = cabinetBaseHeightMm(module);
  if (cabinetBaseType(module) !== "plinth" || height <= 0) return [];
  const [front, ...rest] = plinthPanels(module.width, module.depth, height, cabinetBaseSetbackMm(module));
  const finish = cabinetPlinthFinish(module);
  const finished: BuiltPanel = !finish ? front!
    : finish.kind === "enamel"
      ? { ...front!, painted: true, note: `${front!.note}. МДФ в эмали в цвет фасадов, без фрезеровки` }
      : finish.decor
        ? { ...front!, decor: finish.decor, note: `${front!.note}. В декоре фасадов` }
        : { ...front!, note: `${front!.note}. В декоре фасадов` };
  return [finished, ...rest];
};

/** Монтажные доборы шкафа: боковые на всю высоту с антресолью и верхний. */
export const cabinetFillerPanels = (project: PlannerProject, module: WardrobeModule): BuiltPanel[] => {
  if (module.type !== "single" && module.type !== "double") return [];
  const left = leftFillerMm(module);
  const right = rightFillerMm(module);
  const top = topFillerMm(module);
  const installedHeight = module.height + (mezzanineFor(project, module.id)?.height ?? 0);
  const bottom = cabinetBaseType(module) === "legs" ? cabinetBaseHeightMm(module) : 0;
  const sideHeight = Math.max(0, installedHeight - bottom);
  const note = "Подрезать по фактической геометрии стены";
  return [
    ...(left > 0 ? [panel("Добор левый", 1, sideHeight, left, { note })] : []),
    ...(right > 0 ? [panel("Добор правый", 1, sideHeight, right, { note })] : []),
    ...(top > 0 ? [panel("Добор верхний", 1, module.width + left + right, top, { note })] : [])
  ];
};

/** Неподвижные полки над наружными ящиками — по одной в каждом пролёте. */
export const cabinetDrawerShelfPanels = (module: WardrobeModule): BuiltPanel[] => {
  if (module.type !== "single" && module.type !== "double") return [];
  const layout = cabinetDrawerLayout(module);
  if (!layout) return [];
  return layout.bays.map((bay, index) =>
    panel(`Неподвижная полка над ящиками${layout.bays.length > 1 ? ` · пролёт ${index + 1}` : ""}`, 1,
      bay.innerRight - bay.innerLeft, module.depth));
};

export const cabinetPanels = (project: PlannerProject, module: WardrobeModule): BuiltPanel[] => [
  ...cabinetBodyPanels(module),
  ...cabinetDrawerShelfPanels(module),
  ...cabinetPlinthPanels(module),
  ...cabinetFillerPanels(project, module)
];

// ---------------------------------------------------------------------------
// Угловой шкаф

/**
 * Угловой (28.10): один прямой корпус, уходит вглубь угла за соседний шкаф.
 * Боковины на всю высоту, верх и дно между ними, задняя стенка 8 мм; полки —
 * во всю длину. Цоколь — рамка под корпусом. Добор — в фасадах (в отделке дверей).
 */
export const cornerPanels = (module: WardrobeModule): BuiltPanel[] => {
  if (module.type !== "corner45") return [];
  const plan = cornerPlan(module);
  const base = cabinetBaseHeightMm(module);
  const legs = cabinetBaseType(module) === "legs";
  const sideHeight = legs ? module.height - base : module.height;
  const bodyHeight = Math.max(0, module.height - base - CARCASS_MM * 2);
  const hangingShelves = module.items.filter((item) => item.type === "shelf").length;
  const wall = plan.run === "back" ? "задней" : "боковой";
  const parts = [
    panel("Боковины", 2, sideHeight, plan.d, { note: `Вдоль ${wall} стены, корпус ${plan.length} мм: у стены в углу и у наружного торца` }),
    panel("Верх и дно", 2, plan.length - CARCASS_MM * 2, plan.d),
    panel("Задняя стенка", 1, plan.length - CARCASS_MM * 2, bodyHeight, { thicknessMm: 8, kind: "back" })
  ];
  // Антресоль над угловым — такой же корпус над ним (картинка Сергея, п. 5).
  const mezzanine = cornerMezzanineMm(module);
  if (mezzanine) {
    parts.push(
      panel("Антресоль: боковины", 2, mezzanine, plan.d, { note: "На одной высоте с антресолями соседних шкафов" }),
      panel("Антресоль: верх и дно", 2, plan.length - CARCASS_MM * 2, plan.d),
      panel("Антресоль: задняя стенка", 1, plan.length - CARCASS_MM * 2, Math.max(0, mezzanine - CARCASS_MM * 2), { thicknessMm: 8, kind: "back" })
    );
  }
  // Глухая панель перед глухой частью и стойка под петли у угла — Г-образная
  // деталь (31.10): петли двери — у добора, полки глухой части закрыты спереди.
  const hingePost = cornerHingePostSpan(module);
  const blindPanel = cornerBlindPanelSpan(module);
  if (hingePost && blindPanel) {
    const mezzanineHeight = Math.max(0, mezzanine - CARCASS_MM * 2);
    const panelNote = "Закрывает лицо глухой части: от боковины у стены до стойки под петли, в плоскости лица корпуса, от дна до крыши; полки уходят за неё вглубь угла";
    const postNote = "За добором углового, к торцу глухой панели под прямым углом (Г-образная деталь); на ней монтажные планки петель двери";
    parts.push(panel("Глухая панель", 1, bodyHeight, blindPanel.widthMm, { note: panelNote }));
    parts.push(panel("Стойка под петли", 1, bodyHeight, CORNER_HINGE_POST_DEPTH_MM, { note: postNote }));
    if (mezzanine) {
      parts.push(panel("Антресоль: глухая панель", 1, mezzanineHeight, blindPanel.widthMm, { note: panelNote }));
      parts.push(panel("Антресоль: стойка под петли", 1, mezzanineHeight, CORNER_HINGE_POST_DEPTH_MM, { note: postNote }));
    }
  }
  // Щель за доборами между угловым и соседом — сверху планка вровень с крышами.
  const gapCover = cornerGapCoverSpan(module);
  if (gapCover) {
    parts.push(panel("Планка над щелью у соседа", 1, gapCover.lengthMm, gapCover.widthMm, {
      note: "Сверху, вровень с крышами углового и соседа: закрывает пустую щель за доборами между лицом углового и боковиной соседа"
    }));
  }
  // Полки — в своём декоре, если он выбран (16.10), и по цене его группы.
  const shelfDecor = normalizeShelfDecor(module);
  if (hangingShelves) {
    const shelf = cornerShelfSize(module);
    parts.push(panel("Полки", hangingShelves, shelf.length, shelf.depth, {
      ...(shelfDecor ? { decor: shelfDecor } : {}),
      note: `Во всю длину, вглубь угла за соседний шкаф (глухая часть ${plan.blind} мм)${hingePost ? `; вырез ${CORNER_HINGE_POST_DEPTH_MM} × ${CARCASS_MM + 4} мм под стойку петель` : ""}`
    }));
  }
  if (cabinetBaseType(module) === "plinth" && base > 0) {
    const plinth = cornerPlinthPlan(module);
    // Передняя планка — в цвет фасадов, без фрезеровки, как у прямых шкафов (видео Сергея 23.10).
    const finish = cabinetPlinthFinish(module);
    const front = panel("Цоколь: передняя планка", 1, Math.round(plinth.front.length), base, {
      note: `Утоплена на ${cabinetBaseSetbackMm(module)} мм от фасада${finish ? finish.kind === "enamel" ? "; МДФ в эмали в цвет фасадов, без фрезеровки" : finish.decor ? `; в декоре фасадов ${finish.decor}` : "" : ""}`,
      ...(finish?.kind === "enamel" ? { painted: true as const } : finish?.decor ? { decor: finish.decor } : {})
    });
    parts.push(
      front,
      panel("Цоколь: торцевые планки", 2, Math.round(plinth.endOuter.length), base, { note: "От передней планки до стены" }),
      panel("Цоколь: задняя планка", 1, Math.round(plinth.back.length), base, { note: `У ${wall} стены` })
    );
  }
  return parts;
};

export interface BuiltDoorLeaf {
  widthMm: number;
  heightMm: number;
  thicknessMm: number;
}

/**
 * Высота фасадной зоны: от цоколя (или от шва над наружными ящиками) до верха
 * корпуса, при едином фасаде — до верха антресоли. Створка на 8 мм ниже.
 */
export const cabinetFacadeHeightMm = (project: PlannerProject, module: WardrobeModule) => {
  const base = module.type === "mezzanine" ? 0 : cabinetBaseHeightMm(module);
  const unifiedExtra = hasUnifiedFacade(project, module) ? mezzanineFor(project, module.id)?.height ?? 0 : 0;
  const outerDrawers = module.type === "mezzanine" || module.type === "corner45" ? null : cabinetDrawerLayout(module);
  return Math.max(0, module.height + unifiedExtra - (outerDrawers ? outerDrawers.doorBottomMm - 2 : base));
};

/** Створки шкафа, антресоли или углового — как построены в 3D. */
export const cabinetDoorLeaves = (project: PlannerProject, module: WardrobeModule): BuiltDoorLeaf[] => {
  if (module.door === "open" || module.type === "shelving" || module.type === "end") return [];
  if (module.type === "mezzanine" && hasUnifiedFacade(project, baseModuleFor(project, module))) return [];
  const heightMm = Math.max(220, cabinetFacadeHeightMm(project, module) - 8);
  if (module.door === "sliding") {
    const widthMm = Math.min(MAX_SLIDING_DOOR_WIDTH_MM, module.width / 2);
    return [0, 1].map(() => ({ widthMm, heightMm, thicknessMm: 18 }));
  }
  // Антресоль с откидными фасадами: по фасаду на пролёт, складной HF — из двух частей.
  const flaps = mezzanineFlapLayout(module, moduleHeadroomMm(project, module));
  if (flaps) {
    return flaps.flatMap((flap) => flap.front.partHeightsMm.map((partHeightMm) =>
      ({ widthMm: flap.widthMm, heightMm: partHeightMm, thicknessMm: 20 })));
  }
  // Угловой: дверь корпуса и — если есть — дверь антресоли над ним (28.10).
  if (module.type === "corner45") {
    return hingedDoorLeafLayout(module).map((leaf) => ({ widthMm: leaf.widthMm,
      heightMm: leaf.index === 1 ? Math.max(220, cornerMezzanineMm(module) - 8) : heightMm, thicknessMm: 20 }));
  }
  return hingedDoorLeafLayout(module).map((leaf) => ({ widthMm: leaf.widthMm, heightMm, thicknessMm: 20 }));
};

// ---------------------------------------------------------------------------
// Торцевые полки

export const endModulePanels = (module: WardrobeModule): BuiltPanel[] => {
  if (module.type !== "end") return [];
  const tiers = endModuleTierCount(module);
  const baseType = cabinetBaseType(module);
  const base = cabinetBaseHeightMm(module);
  const sideHeight = Math.max(CARCASS_MM, module.height - (baseType === "legs" ? base : 0));
  const parts = [
    panel(`${endModuleShape(module) === "rounded" ? "Радиусные" : "Скошенные"} горизонтальные панели`, tiers + 1,
      module.width - CARCASS_MM, module.depth - END_MODULE_BACK_MM, {
        shapeAreaM2: endModulePlateAreaM2(module),
        note: "Фигурные; размер заготовки; между боковиной и краем, перед задником"
      }),
    // На опорах боковина и задник начинаются над опорами; задник между
    // боковиной и краем, на толщину боковины уже.
    panel("Примыкающая боковая панель", 1, sideHeight, module.depth),
    panel("Задняя стенка", 1, Math.max(80, module.width - CARCASS_MM), sideHeight, { thicknessMm: 10, kind: "back" })
  ];
  if (baseType === "plinth" && base > 0) {
    const setback = cabinetBaseSetbackMm(module);
    parts.push(
      panel("Цоколь: боковая планка", 1, Math.max(80, module.depth - setback), base, {
        note: `Под примыкающей боковиной, отступ ${setback} мм от лицевой кромки`
      }),
      panel("Цоколь: задняя планка", 1, Math.max(80, module.width - CARCASS_MM), base, { note: "У стены" })
    );
  }
  return parts;
};

// ---------------------------------------------------------------------------
// Стеллаж и комод

const spanRange = (start: number, span: number) => span > 1 ? `${start + 1}–${start + span}` : `${start + 1}`;

export const shelvingCellLabel = (layout: Pick<ShelvingCellLayout, "column" | "row" | "columnSpan" | "rowSpan">) =>
  `яч. ${spanRange(layout.column, layout.columnSpan)} × ${spanRange(layout.row, layout.rowSpan)}${
    layout.columnSpan > 1 || layout.rowSpan > 1 ? " (объединённая)" : ""}`;

const diagonalLabel = (section: { column: number; row: number; columnSpan: number; rowSpan: number }) =>
  `диагональная секция яч. ${spanRange(section.column, section.columnSpan)} × ${spanRange(section.row, section.rowSpan)}`;

/** Цоколь стеллажа: у пола и на ножках его нет. */
export const shelvingHasPlinth = (module: WardrobeModule) =>
  module.shelvingBase !== "floor" && module.shelvingBase !== "legs";

/** Стойки сетки по рядам: внутри диагоналей и объединённых ячеек их нет. */
const shelvingVerticalPanels = (module: WardrobeModule, grid: ShelvingGridLayout): BuiltPanel[] => {
  const spans = getShelvingSpanSections(module, grid);
  return grid.cellHeights.map((height, row) => {
    const count = grid.columnDividerCenters.filter((_, dividerIndex) =>
      !spans.some((section) =>
        dividerIndex >= section.column && dividerIndex < section.column + section.columnSpan - 1 &&
        row >= section.row && row < section.row + section.rowSpan) &&
      getShelvingMainDividerSegment(module, dividerIndex, row, grid) !== null).length;
    return panel(`Вертикальные делители · ряд ${row + 1}`, count, height, module.depth, {
      note: "Между горизонтальными полками"
    });
  }).filter((part) => part.count > 0);
};

/** Полки сетки: целые на всю ширину, над диагоналями и объединёнными ячейками — с вырезом. */
const shelvingHorizontalPanels = (module: WardrobeModule, grid: ShelvingGridLayout): BuiltPanel[] => {
  const spans = getShelvingSpanSections(module, grid);
  const innerLeft = -module.width / 2 + SHELVING_PANEL_THICKNESS_MM;
  const innerRight = module.width / 2 - SHELVING_PANEL_THICKNESS_MM;
  const lengths = new Map<number, number>();
  grid.rowDividerCenters.forEach((_, dividerIndex) => {
    const openings = spans
      .filter((section) => dividerIndex >= section.row && dividerIndex < section.row + section.rowSpan - 1)
      .map((section) => [Math.max(innerLeft, section.left), Math.min(innerRight, section.right)] as const)
      .sort((a, b) => a[0] - b[0]);
    let cursor = innerLeft;
    const pieces: number[] = [];
    for (const [from, to] of openings) {
      if (from - cursor >= 20) pieces.push(from - cursor);
      cursor = Math.max(cursor, to);
    }
    if (innerRight - cursor >= 20) pieces.push(innerRight - cursor);
    pieces.forEach((length) => {
      const key = Math.round(length);
      lengths.set(key, (lengths.get(key) ?? 0) + 1);
    });
  });
  const note = spans.length
    ? "Над объединёнными ячейками и в диагональных секциях полка сетки не ставится"
    : undefined;
  return [...lengths].map(([length, count]) =>
    panel("Горизонтальные полки", count, length, module.depth, note ? { note } : {}));
};

/** Доски диагональной решётки, по секциям и длинам. */
const shelvingLatticePanels = (module: WardrobeModule, grid: ShelvingGridLayout): BuiltPanel[] =>
  getShelvingDiagonalSections(module, grid).flatMap((section) => {
    const lengths = new Map<number, { count: number; depth: number }>();
    shelvingLatticeBoards(section, module.depth).forEach((board) => {
      const key = Math.round(board.length);
      const entry = lengths.get(key) ?? { count: 0, depth: board.depth };
      entry.count += 1;
      lengths.set(key, entry);
    });
    return [...lengths].sort((a, b) => b[0] - a[0]).map(([length, entry]) =>
      panel(`Доски диагональной решётки · ${diagonalLabel(section)}`, entry.count, length, entry.depth, {
        note: "Под углом 58° в обе стороны; в пересечениях пазы вполдерева; торцы опилить под угол по месту"
      }));
  });

/** Общая задняя стенка или задники отдельных ячеек и диагональных секций. */
const shelvingBackPanels = (module: WardrobeModule, grid: ShelvingGridLayout): BuiltPanel[] => {
  const thicknessMm = SHELVING_BACK_PANEL_THICKNESS_MM;
  if (module.shelvingBackPanel) {
    return [panel("Общая задняя стенка", 1, module.width - SHELVING_PANEL_THICKNESS_MM * 2,
      Math.max(0, grid.cellTop - grid.cellBottom), { thicknessMm, kind: "back" })];
  }
  const cells = getShelvingCellLayouts(module, grid)
    .filter((layout) => layout.config?.backPanel)
    .map((layout) => {
      const size = shelvingCellBackPanelSizeMm(layout.width, layout.height);
      return panel(`Задняя стенка · ${shelvingCellLabel(layout)}`, 1, size.width, size.height, { thicknessMm, kind: "back" });
    });
  const diagonals = getShelvingDiagonalSections(module, grid)
    .filter((section) => section.cell.backPanel)
    .map((section) => {
      const size = shelvingCellBackPanelSizeMm(section.width, section.height);
      return panel(`Задняя стенка · ${diagonalLabel(section)}`, 1, size.width, size.height, { thicknessMm, kind: "back" });
    });
  return [...cells, ...diagonals];
};

/** Деталь из МДФ в эмали: по цене эмали, в примечании — цвет (корпус в эмали, 28.10). */
const enamelPanel = (part: BuiltPanel, finish: EnamelFinish): BuiltPanel => {
  const { decor: _decor, ...rest } = part;
  return { ...rest, painted: true, note: `${part.note ? `${part.note}. ` : ""}МДФ в эмали, ${enamelColorLabel(finish)}` };
};

/** Корпус и сетка стеллажа: боковины, крышка, дно, цоколь, стойки, полки, решётка, задники. */
export const shelvingStructurePanels = (module: WardrobeModule, grid = getShelvingGridLayout(module)): BuiltPanel[] => {
  if (module.type !== "shelving") return [];
  const innerWidth = module.width - SHELVING_PANEL_THICKNESS_MM * 2;
  // Комод: столешница может быть в своём декоре, цоколь — в эмали фасадов (15.10).
  const ownTop = normalizeTopDecor(module);
  const enamelPlinth = chestPlinthEnamel(module);
  // Корпус и столешница в эмали (28.10); задние стенки — по-прежнему из плиты.
  const carcassEnamel = shelvingCarcassEnamel(module);
  const topEnamel = chestTopEnamel(module);
  const body = (part: BuiltPanel) => carcassEnamel ? enamelPanel(part, carcassEnamel) : part;
  const topPanel = ownTop || topEnamel
    ? panel("Столешница (верхняя панель)", 1, module.width, module.depth,
        { ...(ownTop && !topEnamel ? { decor: ownTop } : {}),
          note: `Накладная: лежит на боковинах во всю ширину, кромки по периметру ${topEnamel ? "окрашены" : "в декоре столешницы"}` })
    : panel("Верхняя панель (накладная)", 1, module.width, module.depth,
        { note: "Лежит на боковинах во всю ширину, кромка по периметру" });
  return [
    body(panel("Боковые панели", 2, shelvingSideHeightMm(module), module.depth,
      module.shelvingBase === "legs" ? { note: "Начинаются над открытыми опорами" } : {})),
    // Крышка накладная (27.10): лежит на боковинах во всю ширину.
    topEnamel ? enamelPanel(topPanel, topEnamel) : ownTop ? topPanel : body(topPanel),
    body(panel("Нижняя панель", 1, innerWidth, module.depth)),
    ...(shelvingHasPlinth(module)
      ? shelvingPlinthPanels(module).map((part) => enamelPlinth
        ? { ...part, painted: true as const, note: `${part.note ? `${part.note}. ` : ""}МДФ в эмали в цвет фасадов, без фрезеровки` }
        : body(part))
      : []),
    ...shelvingVerticalPanels(module, grid).map(body),
    ...shelvingHorizontalPanels(module, grid).map(body),
    ...shelvingLatticePanels(module, grid).map(body),
    ...shelvingBackPanels(module, grid)
  ];
};

/** Доп. полки и перегородки в видимых ячейках (без стеклянных — они в наполнении). */
export const shelvingCellBoardPanels = (module: WardrobeModule, grid = getShelvingGridLayout(module)): BuiltPanel[] => {
  const carcassEnamel = shelvingCarcassEnamel(module);
  const parts = shelvingCellBoardPanelsRaw(module, grid);
  return carcassEnamel ? parts.map((part) => enamelPanel(part, carcassEnamel)) : parts;
};

const shelvingCellBoardPanelsRaw = (module: WardrobeModule, grid: ShelvingGridLayout): BuiltPanel[] =>
  getShelvingCellLayouts(module, grid).flatMap((layout) => {
    if (!layout.config) return [];
    // Комод (16.10): полка между ящиками и откидным сверху.
    const split = chestCellSplit(module, layout.config, layout, grid);
    return [
      ...getShelvingCellBoards(layout.config, layout.width, layout.height, module.depth)
        .filter((board) => !board.glass)
        .map((board) => panel(`${board.kind === "divider" ? "Перегородка" : "Доп. полка"} · ${shelvingCellLabel(layout)}`, 1,
          board.kind === "divider" ? board.height : board.width, board.depth)),
      ...(split ? [panel(`Полка над ящиками · ${shelvingCellLabel(layout)}`, 1, Math.max(0, layout.width - 2), Math.max(80, module.depth - 10),
        { note: `Отсек под откидным ${Math.round(split.flapClearMm)} мм в свету, под ящики ${Math.round(split.drawersClearMm)} мм` })] : [])
    ];
  });

/** Все плитные детали изделия, как построены (без фасадов и стекла). */
export const moduleBuiltPanels = (project: PlannerProject, module: WardrobeModule): BuiltPanel[] => {
  if (module.type === "end") return endModulePanels(module);
  if (module.type === "shelving") {
    const grid = getShelvingGridLayout(module);
    return [...shelvingStructurePanels(module, grid), ...shelvingCellBoardPanels(module, grid)];
  }
  if (isCabinet(module)) return cabinetPanels(project, module);
  if (module.type === "corner45") return cornerPanels(module);
  return [];
};
